import React, { useState, useEffect, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function TeacherProfile() {
    const [teachers, setTeachers] = useState([]);
    const [filteredTeachers, setFilteredTeachers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedTeacher, setSelectedTeacher] = useState(null);

    useEffect(() => {
        fetchTeachers();
    }, []);

    const fetchTeachers = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/teachers`);
            const data = response.data.data || [];
            setTeachers(data);
            setFilteredTeachers(data);
        } catch (error) {
            console.error('Error fetching teachers:', error);
            setTeachers([]);
            setFilteredTeachers([]);
        } finally {
            setLoading(false);
        }
    };

    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func(...args), delay);
        };
    };

    const filterTeachers = useCallback((search) => {
        if (!search.trim()) {
            setFilteredTeachers(teachers);
            return;
        }
        const searchLower = search.toLowerCase();
        const filtered = teachers.filter(t => 
            (t.firstName && t.firstName.toLowerCase().includes(searchLower)) ||
            (t.teacherId && t.teacherId.toLowerCase().includes(searchLower)) ||
            (t.nic && t.nic.toLowerCase().includes(searchLower)) ||
            (t.lastName && t.lastName.toLowerCase().includes(searchLower))
        );
        setFilteredTeachers(filtered);
    }, [teachers]);

    const debouncedFilter = useCallback(debounce(filterTeachers, 300), [filterTeachers]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        debouncedFilter(value);
    };

    const nameTemplate = (rowData) => {
        const isSelected = selectedTeacher && selectedTeacher.teacherId === rowData.teacherId;
        return (
            <span
                style={{ 
                    cursor: 'pointer', 
                    color: isSelected ? '#fff' : '#2266e3',
                    backgroundColor: isSelected ? '#2266e3' : 'transparent',
                    padding: '2px 5px',
                    borderRadius: '3px',
                    display: 'block'
                }}
                onClick={() => setSelectedTeacher(rowData)}
            >
                {rowData.firstName} {rowData.lastName}
            </span>
        );
    };

    return (
        <div style={{
            position: 'fixed',
            left: '220px',
            top: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            zIndex: 100
        }}>
            {loading && (
                <div className="loading-overlay">
                    <ProgressSpinner />
                </div>
            )}

            {/* Left: Teacher List */}
            <div style={{
                width: '220px',
                minWidth: '220px',
                background: '#fff',
                borderRight: '1px solid #dee2e6',
                overflow: 'auto'
            }}>
                <div style={{ padding: '10px' }}>
                    <span className="p-input-icon-right" style={{ width: '100%' }}>
                        <InputText
                            className={searchText ? "p-filled" : ""}

                            value={searchText}
                            onChange={handleSearchChange}
                            placeholder="Name, NIC, TID..."
                            style={{ width: '100%', fontSize: '0.8rem' }}
                        />{searchText && <i className="pi pi-times" style={{cursor:"pointer"}} onClick={() => {setSearchText(""); setFilteredTeachers(teachers);}} />}{!searchText && <i className="pi pi-search" />}
                    </span>
                </div>
                <DataTable
                    value={filteredTeachers}
                    paginator
                    rows={20}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                    loading={loading}
                    scrollable
                    scrollHeight="calc(100vh - 120px)"
                    emptyMessage="No teachers"
                    size="small"
                >
                    <Column
                        header="Teachers"
                        body={nameTemplate}
                        style={{ fontSize: '0.75rem' }}
                    />
                </DataTable>
            </div>

            {/* Right: Full Profile Area */}
            <div style={{
                flex: 1,
                background: '#f8f9fa',
                padding: '20px',
                overflow: 'auto'
            }}>
                {selectedTeacher ? (
                    <div>
                        <h2 style={{ margin: '0 0 20px 0', color: '#1e3a5f', borderBottom: '2px solid #2266e3', paddingBottom: '10px' }}>
                            Teacher Profile
                        </h2>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', maxWidth: '600px' }}>
                            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <label style={{ color: '#6c757d', fontSize: '0.8rem' }}>Full Name</label>
                                <div style={{ fontSize: '1.1rem', fontWeight: '500', marginTop: '5px' }}>
                                    {selectedTeacher.firstName} {selectedTeacher.lastName}
                                </div>
                            </div>
                            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <label style={{ color: '#6c757d', fontSize: '0.8rem' }}>Teacher ID</label>
                                <div style={{ fontSize: '1.1rem', fontWeight: '500', marginTop: '5px' }}>
                                    {selectedTeacher.teacherId || '-'}
                                </div>
                            </div>
                            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <label style={{ color: '#6c757d', fontSize: '0.8rem' }}>Email</label>
                                <div style={{ fontSize: '1rem', marginTop: '5px', wordBreak: 'break-all' }}>
                                    {selectedTeacher.email || '-'}
                                </div>
                            </div>
                            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <label style={{ color: '#6c757d', fontSize: '0.8rem' }}>Phone</label>
                                <div style={{ fontSize: '1rem', marginTop: '5px' }}>
                                    {selectedTeacher.phone || '-'}
                                </div>
                            </div>
                            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <label style={{ color: '#6c757d', fontSize: '0.8rem' }}>NIC</label>
                                <div style={{ fontSize: '1rem', marginTop: '5px' }}>
                                    {selectedTeacher.nic || '-'}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#6c757d'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <i className="pi pi-user" style={{ fontSize: '4rem', marginBottom: '20px', opacity: 0.3 }}></i>
                            <p>Select a teacher from the list to view profile</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TeacherProfile;
